// ============================================
// Material v2 — ShaderProgram + PropertyBlock + RenderState + Tags
// ============================================

import { type IRenderContext } from '../core/IRenderContext';
import { type GPUTypeInfo, type StructDef, type GPUValue } from '../core/GPUTypes';
import {
  type TextureHandle,
  type PipelineHandle,
  type PipelineDescriptor,
  type OverrideConstants,
  type MaterialTag,
  type RenderState,
  type TextureSlotName,
  type ShaderHandle,
  TextureSlot,
  NULL_HANDLE,
  blendStateFor,
} from '../types';
import { ShaderProgram } from './ShaderProgram';
import { PropertyBlock } from './PropertyBlock';

// ============================================
// Material
// ============================================

/**
 * Material combines a shader program, typed properties, textures,
 * render state and a material tag for pass routing.
 *
 * ### Variant system
 *
 * WebGPU `override` constants — compile-time specialization.
 * The GPU compiler eliminates dead branches:
 *
 *   WGSL: override USE_SHADOWS: bool = false;
 *   WGSL: override MAX_LIGHTS: u32 = 64;
 *   WGSL: if (USE_SHADOWS) { ... // dead-code eliminated when false }
 *
 * ### Example
 * ```ts
 * const LitUniforms = defineStruct('LitUniforms', {
 *   color:     GPUType.f32().vec4(),
 *   intensity: GPUType.f32().build(),
 * });
 *
 * const mat = new Material(ctx, {
 *   program: litShaderWgsl,
 *   overrides: { USE_SHADOWS: true, MAX_LIGHTS: 128 },
 *   uniformDef: LitUniforms,
 *   tag: 'opaque',
 *   renderState: { depthWrite: true },
 *   textureCount: 3,
 * });
 *
 * mat.setProperty('color', [1, 0.5, 0, 1]);
 * mat.setTexture('ALBEDO', albedoTex);
 * mat.bind();
 * ```
 */
export class Material<
  TFields extends Record<string, GPUTypeInfo> = Record<string, GPUTypeInfo>,
> {
  // -----------------------------------------------------------------------
  // Identity
  // -----------------------------------------------------------------------

  /** Unique material instance ID (for batching / comparison). */
  readonly id: number;
  private static _nextId = 1;

  // -----------------------------------------------------------------------
  // Shader
  // -----------------------------------------------------------------------

  /** Underlying shader program with override + caching. */
  readonly program: ShaderProgram;

  /** Whether we own the ShaderProgram (i.e. created from source string). */
  private _ownsProgram: boolean;

  /** WGSL `override` constants — variant mechanism. */
  private _overrides: OverrideConstants;

  // -----------------------------------------------------------------------
  // Properties
  // -----------------------------------------------------------------------

  /** Typed uniform property block (or null if no uniformDef). */
  readonly properties: PropertyBlock<TFields> | null;

  // -----------------------------------------------------------------------
  // Textures
  // -----------------------------------------------------------------------

  /** Texture handles by slot index. */
  private _textures: Map<number, TextureHandle> = new Map();

  /** How many texture slots the pipeline expects. */
  private _textureCount: number;

  // -----------------------------------------------------------------------
  // Render State
  // -----------------------------------------------------------------------

  /** Per-material render state. */
  renderState: RenderState;

  /** Material tag for pass routing. */
  tag: MaterialTag;

  // -----------------------------------------------------------------------
  // Pipeline
  // -----------------------------------------------------------------------

  /** Cached pipeline handle for the current overrides + state (LDR / swap-chain target). */
  private _pipeline: PipelineHandle = NULL_HANDLE;

  /** Cached pipeline for an HDR (rgba16float) target — needed when drawn into the post-process scene buffer. */
  private _pipelineHDR: PipelineHandle = NULL_HANDLE;

  /** Pipeline descriptor template (without shader/constants). */
  private _pipelineDesc: Omit<PipelineDescriptor, 'shader' | 'constants'>;

  /** Dirty flag — set when overrides change. */
  private _pipelineDirty = true;

  // -----------------------------------------------------------------------
  // Context
  // -----------------------------------------------------------------------

  private ctx: IRenderContext;

  // -----------------------------------------------------------------------
  // Constructor
  // -----------------------------------------------------------------------

  constructor(
    ctx: IRenderContext,
    options: MaterialOptions<TFields>,
  ) {
    this.ctx = ctx;
    this.id = Material._nextId++;

    // Shader
    if (options.program instanceof ShaderProgram) {
      this.program = options.program;
      this._ownsProgram = false;
    } else {
      this.program = new ShaderProgram(ctx, options.program);
      this._ownsProgram = true;
    }
    this._overrides = { ...options.overrides };

    // Properties
    this.properties = options.uniformDef
      ? new PropertyBlock<TFields>(options.uniformDef)
      : null;

    // Textures
    this._textureCount = options.textureCount ?? 1;

    // Render state
    this.tag = options.tag ?? 'transparent';
    this.renderState = { ...options.renderState };
    if (this.renderState.blendMode === undefined) {
      this.renderState.blendMode = this.tag === 'opaque' ? 0 : 1; // None / Normal
    }

    // Pipeline descriptor template
    this._pipelineDesc = {
      vertexBuffers: options.vertexBuffers ?? [],
      colorTargets: options.colorTargets ?? [{ format: ctx.getSwapChainFormat() }],
      depthStencil: options.depthStencil,
      primitive: {
        topology: 'triangle-list',
        cullMode: this.renderState.cullMode ?? 'none',
        usesTextures: this._textureCount > 0,
        textureCount: this._textureCount,
      },
      uniformDef: options.uniformDef,
      // group(1) lighting: when set, the pipeline's bind-group layout includes the lighting bindings so the
      // render context can bind the scene's light data for this material draw (see createAndBindTextureGroup).
      hasLighting: options.hasLighting,
      hasForwardPlus: options.hasForwardPlus,
      label: options.label ?? `material-${this.id}`,
    };

    // Bake the blend mode into the pipeline's color targets. Previously bindPipeline() called
    // ctx.setBlendMode(), which is a no-op in the WebGPU backend — so additive/multiply/screen
    // materials silently rendered as standard alpha. A target that already declares an explicit
    // blend keeps it; otherwise it inherits the mode. The mode also joins the pipeline label so
    // materials sharing one ShaderProgram but differing in blend don't collide in its cache.
    const blend = blendStateFor(this.renderState.blendMode!);
    this._pipelineDesc.colorTargets = this._pipelineDesc.colorTargets.map((t) => ({
      ...t,
      blend: t.blend !== undefined ? t.blend : blend,
    }));
    this._pipelineDesc.label = `${this._pipelineDesc.label}-b${this.renderState.blendMode}`;
  }

  // -----------------------------------------------------------------------
  // Override Constants
  // -----------------------------------------------------------------------

  /** Get the current override constants. */
  get overrides(): Readonly<OverrideConstants> {
    return this._overrides;
  }

  /** Set an override constant. Invalidates the cached pipeline. */
  setOverride(name: string, value: number | boolean): this {
    this._overrides[name] = value;
    this._pipelineDirty = true;
    return this;
  }

  /** Remove an override constant. Invalidates the cached pipeline. */
  removeOverride(name: string): this {
    delete this._overrides[name];
    this._pipelineDirty = true;
    return this;
  }

  /** Replace all override constants. Invalidates the cached pipeline. */
  setOverrides(overrides: OverrideConstants): this {
    this._overrides = { ...overrides };
    this._pipelineDirty = true;
    return this;
  }

  // -----------------------------------------------------------------------
  // Property Access
  // -----------------------------------------------------------------------

  /** Set a uniform property by name. */
  setProperty<K extends keyof TFields & string>(name: K, value: GPUValue): this {
    if (this.properties) {
      this.properties.set(name, value);
    }
    return this;
  }

  /** Set multiple uniform properties at once. */
  setProperties(values: { [K in keyof TFields]?: GPUValue }): this {
    if (this.properties) {
      this.properties.setAll(values);
    }
    return this;
  }

  // -----------------------------------------------------------------------
  // Texture Access
  // -----------------------------------------------------------------------

  /** Number of texture slots this material's pipeline expects (so callers can bind all of them). */
  get textureCount(): number {
    return this._textureCount;
  }

  /** Set a texture by named slot ('ALBEDO', 'NORMAL', 'EMISSION', 'MASK'). */
  setTexture(slot: TextureSlotName, texture: TextureHandle): this {
    this._textures.set(TextureSlot[slot], texture);
    return this;
  }

  /** Set a texture by numeric slot index. */
  setTextureSlot(slot: number, texture: TextureHandle): this {
    this._textures.set(slot, texture);
    return this;
  }

  /** Get texture handle at a given slot. */
  getTexture(slot: TextureSlotName | number): TextureHandle {
    const idx = typeof slot === 'number' ? slot : TextureSlot[slot];
    return this._textures.get(idx) ?? NULL_HANDLE;
  }

  // -----------------------------------------------------------------------
  // Pipeline
  // -----------------------------------------------------------------------

  /** HDR (rgba16float) variant of the pipeline descriptor — same state, different color-target format + label
   * (the ShaderProgram cache keys by label, so a distinct label yields a distinct pipeline). */
  private hdrDesc(): Omit<PipelineDescriptor, 'shader' | 'constants'> {
    const targets = this._pipelineDesc.colorTargets ?? [{ format: 'rgba16float' as const }];
    return {
      ...this._pipelineDesc,
      colorTargets: targets.map((t) => ({ ...t, format: 'rgba16float' as const })),
      label: `${this._pipelineDesc.label ?? `material-${this.id}`}-hdr`,
    };
  }

  /**
   * Ensure the pipeline is created for the current overrides + state.
   * @param hdr — target is an HDR (rgba16float) render target (post-process scene buffer) vs the LDR swap chain.
   */
  ensurePipeline(hdr = false): PipelineHandle {
    if (this._pipelineDirty) {
      this._pipeline = NULL_HANDLE;
      this._pipelineHDR = NULL_HANDLE;
      this._pipelineDirty = false;
    }
    const ov = Object.keys(this._overrides).length > 0 ? this._overrides : undefined;
    if (hdr) {
      if (this._pipelineHDR === NULL_HANDLE) this._pipelineHDR = this.program.getPipeline(ov, this.hdrDesc());
      return this._pipelineHDR;
    }
    if (this._pipeline === NULL_HANDLE) this._pipeline = this.program.getPipeline(ov, this._pipelineDesc);
    return this._pipeline;
  }

  /** Get the pipeline handle (creates if needed). */
  get pipeline(): PipelineHandle {
    return this.ensurePipeline();
  }

  // -----------------------------------------------------------------------
  // Bind
  // -----------------------------------------------------------------------

  /**
   * Bind this material for rendering:
   * 1. Set pipeline (override constants + blend mode baked in)
   * 2. Apply uniform properties
   * 3. Bind textures
   */
  bind(): void {
    this.bindPipeline();
    this.bindTextures();
  }

  /**
   * Bind pipeline + blend mode + apply uniform PROPERTIES (not textures). Split from {@link bindTextures}
   * so a batch can write the remaining uniforms (e.g. uViewProjection/uTime) and `captureUniforms()` BEFORE
   * the texture bind group is built — the group-0 bind group (made in bindTexture) captures the uniform
   * buffer at creation, so binding textures last lets each material draw reference its own snapshot.
   */
  bindPipeline(hdr = false, ctx: IRenderContext = this.ctx): void {
    // Bind through the CALLER's context (the one with the active render pass), not necessarily the context
    // this material was built on — a material built on the editor context is also drawn by the GameView's
    // own context (device + handle maps are shared via sharedContext). The pipeline is still *created* on
    // this material's own ctx (ensurePipeline → program), which is fine: the handle resolves on both.
    // Blend mode is baked into the pipeline's color targets at construction (see the constructor),
    // so there's no per-bind setBlendMode — that call was a no-op in the WebGPU backend anyway.
    ctx.setPipeline(this.ensurePipeline(hdr));
    if (this.properties) {
      this.properties.apply(ctx);
    }
  }

  /**
   * Bind this material's textures — builds the group-0 bind group. Call AFTER `captureUniforms()`.
   * `ctx` defaults to the material's own context but must be the caller's active-pass context when the
   * material is drawn through a different (shared-resource) context — e.g. the GameView (see {@link bindPipeline}).
   */
  bindTextures(ctx: IRenderContext = this.ctx): void {
    for (const [slot, texture] of this._textures) {
      ctx.bindTexture(slot, texture);
    }
  }

  // -----------------------------------------------------------------------
  // Clone
  // -----------------------------------------------------------------------

  /** Clone this material (shares ShaderProgram, copies properties + textures). */
  clone(): Material<TFields> {
    const m = new Material<TFields>(this.ctx, {
      program: this.program,
      overrides: { ...this._overrides },
      uniformDef: this.properties?.structDef,
      tag: this.tag,
      renderState: { ...this.renderState },
      vertexBuffers: this._pipelineDesc.vertexBuffers,
      colorTargets: this._pipelineDesc.colorTargets,
      depthStencil: this._pipelineDesc.depthStencil,
      textureCount: this._textureCount,
      label: `${this._pipelineDesc.label}-clone`,
    });

    // Copy textures
    for (const [slot, tex] of this._textures) {
      m._textures.set(slot, tex);
    }

    // Copy uniform values
    if (this.properties && m.properties) {
      const srcBuf = new Uint8Array(this.properties.buffer);
      const dstBuf = new Uint8Array(m.properties.buffer);
      dstBuf.set(srcBuf);
    }

    return m;
  }

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  /** Destroy GPU resources owned by this material. */
  destroy(): void {
    this.properties?.destroy();
    if (this._ownsProgram) {
      this.program.destroy();
    }
  }
}

// ============================================
// Options
// ============================================

/**
 * Options for creating a Material.
 */
export interface MaterialOptions<
  TFields extends Record<string, GPUTypeInfo> = Record<string, GPUTypeInfo>,
> {
  /** WGSL source string OR an existing ShaderProgram instance. */
  program: string | ShaderProgram;

  /**
   * WGSL `override` constants — the primary variant mechanism.
   * Feature toggles, numeric config. GPU compiler eliminates dead code.
   *
   * ```ts
   * overrides: { USE_SHADOWS: true, MAX_LIGHTS: 128 }
   * ```
   */
  overrides?: OverrideConstants;

  /** Uniform struct definition (from defineStruct). Creates a PropertyBlock. */
  uniformDef?: StructDef<TFields>;

  /** Number of texture slots the pipeline expects (default: 1). */
  textureCount?: number;

  /** Material tag for pass routing (default: 'transparent'). */
  tag?: MaterialTag;

  /** Per-material render state overrides. */
  renderState?: RenderState;

  /** Vertex buffer layouts for the pipeline. */
  vertexBuffers?: PipelineDescriptor['vertexBuffers'];

  /** Color target states for the pipeline. */
  colorTargets?: PipelineDescriptor['colorTargets'];

  /** Depth/stencil state for the pipeline. */
  depthStencil?: PipelineDescriptor['depthStencil'];

  /** Include the group(1) forward-lit lighting bind group (LightUniforms + lights[]). */
  hasLighting?: boolean;

  /** Include the group(1) Forward+ lighting bind group (lights + tiles + shadow + IES). Supersedes hasLighting. */
  hasForwardPlus?: boolean;

  /** Debug label. */
  label?: string;
}

// ============================================
// Shader (lightweight convenience wrapper)
// ============================================

/**
 * Shader wrapper for loading and managing a single shader.
 * For variant support, use `ShaderProgram` instead.
 */
export class Shader {
  private ctx: IRenderContext;
  readonly handle: ShaderHandle;

  constructor(ctx: IRenderContext, vertexSource: string, fragmentSource: string) {
    this.ctx = ctx;
    this.handle = ctx.createShader(vertexSource, fragmentSource);
  }

  destroy(): void {
    this.ctx.destroyShader(this.handle);
  }
}
